import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { ServiceConfig } from '../types'
import { getServiceIcon } from '../utils'

interface ComingSoonServicePageProps {
  service: ServiceConfig
  onBack: () => void
}

export const ComingSoonServicePage: React.FC<ComingSoonServicePageProps> = ({ service, onBack }) => {
  const [email, setEmail] = useState('')
  const [isSubscribed, setIsSubscribed] = useState(false)

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      // Here you would typically send the email to your backend
      console.log(`Notify ${email} about ${service.name}`)
      setIsSubscribed(true)
      setTimeout(() => {
        setEmail('')
        setIsSubscribed(false)
      }, 3000)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-white/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Services
            </button>

            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                <img
                  src="/mernaki-32.png"
                  alt="Mernaki Logo"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="text-xl font-bold text-gray-900">Mernaki</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          {/* Service Icon */}
          <motion.div
            className="inline-block p-6 rounded-3xl mb-8 shadow-2xl"
            style={{ backgroundColor: service.accentColor }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-6xl text-white">
              {getServiceIcon(service.icon)}
            </span>
          </motion.div>

          {/* Coming Soon Badge */}
          <motion.div
            className="inline-block mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="px-6 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-full shadow-lg text-lg">
              🚀 Coming Soon
            </span>
          </motion.div>

          {/* Service Title */}
          <motion.h1
            className="text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {service.name}
          </motion.h1>

          {/* Service Description */}
          <motion.p
            className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {service.description}
          </motion.p>

          {/* Features Preview */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/50"
              >
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                    style={{ backgroundColor: service.accentColor }}
                  />
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Notification Signup */}
          <motion.div
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/50 max-w-md mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Be the First to Know</h3>
            <p className="text-gray-600 mb-6">
              Get notified when {service.name} launches and be among the first to access this powerful service.
            </p>

            {!isSubscribed ? (
              <form onSubmit={handleNotifyMe} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300"
                  required
                />
                <button
                  type="submit"
                  className="w-full px-6 py-3 font-semibold text-white rounded-lg shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
                  style={{ backgroundColor: service.accentColor }}
                >
                  Notify Me When Available
                  <svg className="ml-2 w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-12" />
                  </svg>
                </button>
              </form>
            ) : (
              <motion.div
                className="text-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">You're All Set!</h4>
                <p className="text-gray-600">
                  We'll notify you as soon as {service.name} is available.
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Expected Launch Timeline */}
          <motion.div
            className="mt-12 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            <h4 className="text-lg font-bold text-gray-900 mb-2">Expected Launch</h4>
            <p className="text-gray-600">
              We're working hard to bring you {service.name}. Expected launch: Q1 2026
            </p>
            <div className="mt-4 bg-white/50 rounded-full h-2 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
                initial={{ width: '0%' }}
                animate={{ width: '75%' }}
                transition={{ duration: 2, delay: 1.5, ease: 'easeOut' }}
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">Development Progress: 75%</p>
          </motion.div>
        </div>
      </main>
    </div>
  )
}